import { useState } from "react";
import { Type, Loader2, Copy, Check, RotateCcw, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import AdUnit from "../components/AdUnit";
import axios from "axios";

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

const MODES = [
  { value: "smart", label: "Smart Case (AI)", desc: "AI-powered intelligent casing" },
  { value: "sentence", label: "Sentence Case", desc: "First letter of each sentence" },
  { value: "title", label: "Title Case", desc: "First letter of each word" },
  { value: "upper", label: "UPPER CASE", desc: "All uppercase" },
  { value: "lower", label: "lower case", desc: "All lowercase" },
];

const EXAMPLE_TEXT = `hello WORLD. this is a TEST of the CASE fixer tool. john smith works at NASA and lives in new york city. the iPhone 15 pro max is REALLY expensive. dr. sarah JOHNSON published her RESEARCH on AI and machine learning at MIT.`;

export default function CaseFixer() {
  const [text, setText] = useState("");
  const [mode, setMode] = useState("smart");
  const [fixed, setFixed] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const handleFix = async () => {
    if (!text.trim()) return;
    setLoading(true);
    setError("");
    setFixed("");

    try {
      const res = await axios.post(`${API}/case-fix`, { text: text.trim(), mode });
      setFixed(res.data.fixed);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fix case. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(fixed);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      <section className="py-12 md:py-16 bg-[#F8FAFC] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#FFF1F2] flex items-center justify-center">
              <Type className="w-5 h-5 text-[#E11D48]" />
            </div>
            <span className="label-mono">Case Fixer</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="case-page-title">
            Smart case fixer
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl">
            Fix messy text casing with AI. Handles proper nouns, acronyms, and brand names intelligently.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input */}
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <label className="label-mono">Input Text</label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="w-[180px] bg-slate-50 border-transparent h-9 rounded-md text-sm" data-testid="case-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      <div>
                        <span className="font-medium">{m.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea
              value={text}
              onChange={(e) => { setText(e.target.value); setFixed(""); }}
              placeholder="Paste text with messy casing here..."
              className="min-h-[250px] text-sm bg-slate-50 border-transparent focus:border-[#E11D48] focus:bg-white rounded-md resize-none"
              data-testid="case-input"
            />
            <div className="flex flex-wrap gap-3">
              <Button onClick={handleFix} disabled={loading || !text.trim()} className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-md px-6 h-11 font-medium btn-press" data-testid="fix-case-button">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Fixing...</> : <><Sparkles className="w-4 h-4 mr-2" />Fix Case</>}
              </Button>
              <Button variant="outline" onClick={() => { setText(EXAMPLE_TEXT); setFixed(""); }} className="rounded-md h-11 px-4 border-slate-300 text-slate-600 hover:border-[#E11D48] hover:text-[#E11D48] btn-press" data-testid="load-case-example">
                Load Example
              </Button>
              {(text || fixed) && (
                <Button variant="outline" onClick={() => { setText(""); setFixed(""); setError(""); }} className="rounded-md h-11 px-4 border-slate-300 text-slate-600 btn-press" data-testid="case-reset-button">
                  <RotateCcw className="w-4 h-4 mr-1.5" />Clear
                </Button>
              )}
            </div>
            {error && <p className="text-sm text-red-500" data-testid="case-error">{error}</p>}
          </div>

          {/* Output */}
          <div className="space-y-5">
            <div className="border border-slate-200 rounded-lg bg-white min-h-[350px]">
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <span className="label-mono">Fixed Text</span>
                {fixed && (
                  <button onClick={handleCopy} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#E11D48] transition-colors" data-testid="copy-case-button">
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                )}
              </div>
              <div className="p-5" data-testid="case-output">
                {loading && (
                  <div className="flex items-center gap-3 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Analyzing casing...</span>
                  </div>
                )}
                {!loading && !fixed && (
                  <div className="text-sm text-slate-400 flex flex-col items-center justify-center py-16">
                    <Type className="w-10 h-10 text-slate-300 mb-3" />
                    <p>Your fixed text will appear here</p>
                  </div>
                )}
                {fixed && (
                  <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{fixed}</div>
                )}
              </div>
            </div>
            <AdUnit format="horizontal" />
          </div>
        </div>
      </div>
    </div>
  );
}
